import {useState} from 'react';
import {Table, Button, Tag, Modal, Select} from 'antd';
import {MenuOutlined, PlusOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useParams} from 'react-router-dom';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';
import {
  GET_QUICK_ACTION_CATEGORY_BY_ID,
  DELETE_QUICK_ACTION,
  REORDER_QUICK_ACTIONS,
  GET_ALL_QUICK_ACTIONS,
  REMOVE_QUICK_ACTION_FROM_CATEGORY,
  ADD_QUICK_ACTION_TO_CATEGORY,
} from './resolvers';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import styles from './quickActions.module.scss';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

const {Option} = Select;

const DragHandle = SortableHandle(({className}) => (
  <div className={className}>
    <MenuOutlined />
  </div>
));

const SortableItem = SortableElement(props => <tr {...props} />);
const SortableBody = SortableContainer(props => <tbody {...props} />);

const CategoryActionsTab = () => {
  const {id: categoryId} = useParams();
  const [pendingReorder, setPendingReorder] = useState(null);
  const [reorderModal, setReorderModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    actionId: null,
  });
  const [removeModal, setRemoveModal] = useState({
    open: false,
    actionId: null,
  });
  const [addModal, setAddModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.QuickActions),
  );
  const {data, loading, refetch} = useQuery(GET_QUICK_ACTION_CATEGORY_BY_ID, {
    variables: {categoryId},
    skip: !categoryId,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const {data: allActionsData, loading: loadingAllActions} = useQuery(
    GET_ALL_QUICK_ACTIONS,
    {
      variables: {
        filter: {
          isDefault: false,
        },
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
  );

  const [addActionToCategory, {loading: addingToCategory}] = useMutation(
    ADD_QUICK_ACTION_TO_CATEGORY,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [removeActionFromCategory, {loading: removingFromCategory}] =
    useMutation(REMOVE_QUICK_ACTION_FROM_CATEGORY, {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    });

  const [deleteAction, {loading: deleting}] = useMutation(DELETE_QUICK_ACTION, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [reorderActions, {loading: reordering}] = useMutation(
    REORDER_QUICK_ACTIONS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const category =
    data?.getQuickActionCategoryById?.__typename ===
    'ResponseWithQuickActionCategory'
      ? data?.getQuickActionCategoryById?.data
      : null;

  const originalActions = category?.quickActions || [];

  // Use pending reorder if available, otherwise use original actions
  const actions = pendingReorder || originalActions;

  const onSortEnd = ({oldIndex, newIndex}) => {
    if (oldIndex === newIndex) return;

    const currentList = pendingReorder || originalActions;
    const reorderedActions = arrayMoveImmutable(
      currentList.slice(),
      oldIndex,
      newIndex,
    );

    setPendingReorder(reorderedActions);
  };

  const handleConfirmReorder = () => {
    if (!pendingReorder) return;

    const positions = pendingReorder.map((action, index) => ({
      id: action.id,
      position: index + 1,
    }));

    reorderActions({
      variables: {
        positions,
      },
    })
      .then(({data: {reorderQuickActions}}) => {
        if (
          reorderQuickActions.__typename === 'Error' ||
          reorderQuickActions.statusCode === 400
        ) {
          toast.error(reorderQuickActions.message);
        } else {
          toast.success('Actions reordered successfully.');
          setPendingReorder(null);
          setReorderModal(false);
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const handleCancelReorder = () => {
    setPendingReorder(null);
    setReorderModal(false);
  };

  const handleAddAction = () => {
    if (!selectedActionId) return;

    addActionToCategory({
      variables: {
        quickActionId: selectedActionId,
        categoryId: categoryId,
      },
    })
      .then(({data: {addQuickActionToCategory}}) => {
        if (
          addQuickActionToCategory.__typename === 'Error' ||
          addQuickActionToCategory.statusCode === 400
        ) {
          toast.error(addQuickActionToCategory.message);
        } else {
          toast.success('Action added to category successfully.');
          setAddModal(false);
          setSelectedActionId(null);
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const handleRemoveAction = () => {
    if (!removeModal.actionId) return;

    removeActionFromCategory({
      variables: {
        quickActionId: removeModal.actionId,
      },
    })
      .then(({data: {removeQuickActionFromCategory}}) => {
        if (
          removeQuickActionFromCategory.__typename === 'Error' ||
          removeQuickActionFromCategory.statusCode === 400
        ) {
          toast.error(removeQuickActionFromCategory.message);
        } else {
          toast.success('Action removed from category successfully.');
          setRemoveModal({open: false, actionId: null});
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const handleDelete = () => {
    if (!deleteModal.actionId) return;

    deleteAction({
      variables: {
        quickActionId: deleteModal.actionId,
      },
    })
      .then(({data: {deleteQuickAction}}) => {
        if (
          deleteQuickAction.__typename === 'Error' ||
          deleteQuickAction.statusCode === 400
        ) {
          toast.error(deleteQuickAction.message);
          setDeleteModal({open: false, actionId: null});
        } else {
          toast.success('Action deleted successfully.');
          setDeleteModal({open: false, actionId: null});
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const DraggableContainer = props => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({...restProps}) => {
    const index = actions.findIndex(x => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const columns = [
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 120,
      render: (_, record, index) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <DragHandle className={styles.dragHandle} />
          <span>{pendingReorder ? index + 1 : record.position}</span>
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: key => <Tag>{key || '—'}</Tag>,
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: icon =>
        icon ? (
          <img
            src={icon}
            alt="Icon"
            style={{width: 40, height: 40, objectFit: 'contain'}}
          />
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: language => <Tag>{language || '—'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: isActive => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{display: 'flex', gap: '8px'}}>
          <Button
            type="link"
            danger
            disabled={!tabOperations?.delete}
            onClick={() => setRemoveModal({open: true, actionId: record.id})}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
        <h3>Category Actions</h3>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          {pendingReorder && (
            <>
              <Button
                type="primary"
                onClick={() => setReorderModal(true)}
                style={{background: '#52c41a', borderColor: '#52c41a'}}
                disabled={!tabOperations?.update}>
                Save Order Changes
              </Button>
              <Button onClick={handleCancelReorder}>Discard Changes</Button>
            </>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!tabOperations?.update}
            onClick={() => setAddModal(true)}>
            Add Action
          </Button>
        </div>
      </div>

      <Table
        className="antd-table"
        dataSource={actions}
        columns={columns}
        loading={loading || reordering}
        rowKey="id"
        pagination={false}
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
      />

      <ConfirmModal
        openModal={deleteModal.open}
        setOpenModal={() => setDeleteModal({open: false, actionId: null})}
        loading={deleting}
        title="Delete Action"
        confirmText="Are you sure you want to delete this action? This action cannot be undone!"
        handleOperation={handleDelete}
      />

      <ConfirmModal
        openModal={removeModal.open}
        setOpenModal={() => setRemoveModal({open: false, actionId: null})}
        loading={removingFromCategory}
        title="Remove Action from Category"
        confirmText="Are you sure you want to remove this action from this category? The action will still exist but will no longer be associated with this category."
        handleOperation={handleRemoveAction}
      />

      <ConfirmModal
        openModal={reorderModal}
        setOpenModal={setReorderModal}
        loading={reordering}
        title="Confirm Reorder"
        confirmText="Are you sure you want to save the new order of actions?"
        handleOperation={handleConfirmReorder}
      />

      <Modal
        title="Add Action to Category"
        open={addModal}
        onCancel={() => {
          setAddModal(false);
          setSelectedActionId(null);
        }}
        onOk={handleAddAction}
        confirmLoading={addingToCategory}
        okText="Add Action"
        destroyOnClose>
        <div style={{padding: '20px 0'}}>
          <p style={{marginBottom: 8}}>
            Select an action to add to this category:
          </p>
          <Select
            style={{width: '100%'}}
            placeholder="Select an action"
            loading={loadingAllActions}
            onChange={setSelectedActionId}
            value={selectedActionId}
            showSearch
            optionFilterProp="children">
            {allActionsData?.getAllQuickActions?.data
              ?.filter(action => !action.category?.id)
              .map(action => (
                <Option key={action.id} value={action.id}>
                  {action.name} ({action.key})
                </Option>
              ))}
          </Select>
          <p style={{marginTop: 12, fontSize: 12, color: '#8b82a7'}}>
            * Only non-default actions without a category are shown.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CategoryActionsTab;
